import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import _ from 'lodash';

import { BaseDebugContainer } from './LookupDebuggingStyles';
import ExpandCollapsePayload from './ExpandCollapsePayload';
import { LookupDebugResponse } from 'src/actions/integrations/lookupAdapterDebugging/lookupDebuggerBasicResource';

import { AlertError, AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import { Row } from 'src/presentation/components/forms/Layout';

export enum ErrorCause {
  AUTOLINK_EMPTY_QUERY = 'autolinkEmptyQuery',
  AUTOLINK_NO_CANDIDATES = 'autolinkNoCandidates',
  AUTOLINK_NO_FIELDS = 'autolinkNoFields',
  AUTOLINK_NO_RESULTS = 'autolinkNoResults',
  AUTOLINK_UNLINKED = 'autolinkUnlinked',
  CUSTOM_ATTR_MISSING = 'customAttributeMissing',
  CUSTOM_ATTR_EXTRA = 'customAttributeExtra',
  CUSTOM_ATTR_NOT_STRING = 'customAttributeNotString',
  CUSTOM_ATTR_SHAPE = 'customAttributeShape',
  EXTERNAL_CUSTOMER_ID = 'externalCustomerId',
  EMAILS_SINGULAR = 'emailsSingular',
  PHONES_SINGULAR = 'phonesSingular',
  MARSHAL = 'marshal',
  RESULTS_ARRAY = 'resultsArray',
  RESULTS_INVALID_FORMAT = 'resultsInvalidFormat',
  RESULTS_TOO_MANY = 'tooManyResults',
}

enum Severity {
  CRITICAL = 'CRITICAL',
  WARN = 'WARN',
}

export type ValidationError = {
  attr: ErrorCause;
  severity: string;
  errorMessage: string;
};

type ResponseContainer = {
  response: LookupDebugResponse;
  requestType: string;
  rawResponse: any;
  validationErrors: ValidationError[];
};

const ResponseContainer: FC<ResponseContainer> = ({ response, requestType, rawResponse, validationErrors }) => {
  const numResults = response?.payload?.results?.length;

  const noErrorComponent = <StyledRow data-aid="no-errors-message">No errors were detected!</StyledRow>;

  const validationComponents = _.map(validationErrors, (error, i) => {
    const icon =
      error.severity === Severity.CRITICAL ? (
        <StyledErrorIcon className={'styled-alerticon'} />
      ) : (
        <StyledWarnIcon className={'styled-alerticon'} />
      );
    let errorComponent;

    if (error.attr === ErrorCause.RESULTS_ARRAY || error.attr === ErrorCause.MARSHAL) {
      errorComponent = <RowText>{error.errorMessage} Expand the response to verify the structure.</RowText>;
    } else if (Object.values(ErrorCause).includes(error.attr)) {
      errorComponent = <RowText>{error.errorMessage}</RowText>;
    } else {
      errorComponent = <RowText>An unexpected error occurred.</RowText>;
    }

    return (
      <StyledRow data-aid={`debug-validation-error-${i}`} key={`debug-validation-error-${i}`}>
        {icon}
        {errorComponent}
      </StyledRow>
    );
  });

  const responseCodeText = 'The response code was ' + response?.statusCode;
  const adapterErr = response?.adapterError;
  const adapterErrorComp = adapterErr ? (
    <>
      <StyledRow>
        <StyledErrorIcon />
        <RowText data-aid="adapter-error-message">
          The lookup adapter returned an unexpected response. {response?.statusCode ? responseCodeText : null}{' '}
          {adapterErr?.attrs?.body}
        </RowText>
      </StyledRow>
    </>
  ) : null;

  return (
    <>
      <DebugStepContainer>
        <b data-aid={`${requestType}-results-message`}>
          The {requestType} response returned {numResults} result{numResults !== 1 && 's'}
        </b>
        {(validationErrors && validationErrors.length > 0) || adapterErr ? (
          <div>
            <RowText>The following errors were detected:</RowText>
            {adapterErrorComp}
            {validationComponents}
          </div>
        ) : (
          noErrorComponent
        )}
        {rawResponse ? (
          <ExpandCollapsePayload payload={rawResponse} lookupType={requestType} payloadType="response" />
        ) : null}
      </DebugStepContainer>
    </>
  );
};

const DebugStepContainer = styled.div`
  ${BaseDebugContainer}
  margin: 12px 0 12px 24px;
`;

const BaseAlertIcon = css`
  padding: 8px;
  width: 32px;
  height: 32px;
  min-height: 16px;

  &.styled-alerticon {
    margin-right: 8px;
  }
`;

export const StyledErrorIcon = styled(AlertError)`
  ${BaseAlertIcon}
`;

const StyledWarnIcon = styled(AlertWarning)`
  ${BaseAlertIcon}
`;

const StyledRow = styled(Row)`
  margin-top: 4px;
  margin-bottom: 4px;
`;

export const RowText = styled.div`
  padding-top: 8px;
  padding-bottom: 4px;
`;

export default ResponseContainer;
